import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  DialogActions,
  Button as MuiButton
} from "@mui/material";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography
} from "@mui/material";

import { useState } from "react";
import { Button } from "react-admin";
import { runeTheme } from "src/components/common/RuneTheme";
import {
  MedicationOption,
  MedicationTagGroup
} from "src/providers/categoryMetadata";
import { useCategoryMetadata } from "src/providers/CategoryMetadataContext";

export interface DataTypeConfig {
  enabled: boolean;
  threshold?: number;
}

export interface SelectedMedication {
  name: string;
  classification: string;
}

export interface SelectedMedicationGroup {
  tagName: string;
  displayName: string;
  medicationEnums: string[];
}

export interface MedicationDataTypeConfig extends DataTypeConfig {
  showAll?: boolean;
  selectedMedications?: SelectedMedication[];
  selectedGroups?: SelectedMedicationGroup[];
}

interface VisualizationConfig {
  highlightMissingData: boolean;
}

export interface ProjectDisplayConfig {
  dataTypes: {
    tremor_dyskinesia: DataTypeConfig;
    mobility: DataTypeConfig;
    sleep: DataTypeConfig;
    medication: MedicationDataTypeConfig;
    tap_test: DataTypeConfig;
    bowel_movement: DataTypeConfig;
  };
  visualization: VisualizationConfig;
}

interface ProjectDisplayConfigurationDialogProps {
  projectDisplayConfig: ProjectDisplayConfig;
  setProjectDisplayConfig: (config: ProjectDisplayConfig) => void;
}

export const ProjectDisplayConfigurationDialog = ({
  projectDisplayConfig,
  setProjectDisplayConfig
}: ProjectDisplayConfigurationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [tempConfig, setTempConfig] = useState(projectDisplayConfig);

  const handleConfigurationChange = (config: ProjectDisplayConfig) => {
    setTempConfig(config);
  };

  const handleClickOpen = () => {
    setTempConfig(projectDisplayConfig);
    setOpen(true);
  };

  const handleClickClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    setProjectDisplayConfig(tempConfig);
    setOpen(false);
  };

  return (
    <>
      <Button
        sx={{
          fontSize: "16px",
          lineHeight: "19px",
          letterSpacing: "-0.03em",
          textTransform: "capitalize",
          fontWeight: "600",
          color: runeTheme.palette.button.tertiary.text
        }}
        label="Configure Display"
        onClick={handleClickOpen}
      />
      <Dialog
        open={open}
        onClose={handleClickClose}
        fullWidth
        sx={{ margin: "auto" }}
      >
        <DialogTitle>Display Configuration</DialogTitle>
        <DialogContent>
          <ProjectDisplayConfigurationForm
            projectDisplayConfig={tempConfig}
            onConfigurationChange={handleConfigurationChange}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleClickClose}>Cancel</MuiButton>
          <MuiButton onClick={handleSave} color="primary">
            Save
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ProjectDisplayConfigurationFormProps {
  projectDisplayConfig: ProjectDisplayConfig;
  onConfigurationChange: (config: ProjectDisplayConfig) => void;
}

const DISPLAY_LABELS: Record<string, string> = {
  tremor_dyskinesia: "Tremor & Dyskinesia",
  mobility: "Mobility",
  sleep: "Sleep",
  medication: "Medication",
  tap_test: "Tap Test",
  bowel_movement: "Bowel Movement"
};

const ProjectDisplayConfigurationForm = ({
  projectDisplayConfig,
  onConfigurationChange
}: ProjectDisplayConfigurationFormProps) => {
  const {
    medicationOptions,
    medicationTagGroups: tagGroupOptions,
    loading: loadingMedications
  } = useCategoryMetadata();

  const handleCheckboxChange =
    (dataType: keyof ProjectDisplayConfig["dataTypes"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedConfig = {
        ...projectDisplayConfig,
        dataTypes: {
          ...projectDisplayConfig.dataTypes,
          [dataType]: {
            ...projectDisplayConfig.dataTypes[dataType],
            enabled: event.target.checked
          }
        }
      };
      onConfigurationChange(updatedConfig);
    };

  const handleThresholdChange =
    (dataType: keyof ProjectDisplayConfig["dataTypes"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedConfig = {
        ...projectDisplayConfig,
        dataTypes: {
          ...projectDisplayConfig.dataTypes,
          [dataType]: {
            ...projectDisplayConfig.dataTypes[dataType],
            threshold: parseInt(event.target.value)
          }
        }
      };
      onConfigurationChange(updatedConfig);
    };

  const handleMedicationSelectionChange = (newValue: MedicationOption[]) => {
    const updatedConfig = {
      ...projectDisplayConfig,
      dataTypes: {
        ...projectDisplayConfig.dataTypes,
        medication: {
          ...projectDisplayConfig.dataTypes.medication,
          selectedMedications: newValue.map((opt) => ({
            name: opt.name,
            classification: opt.classification
          }))
        }
      }
    };
    onConfigurationChange(updatedConfig);
  };

  const handleGroupSelectionChange = (newValue: MedicationTagGroup[]) => {
    const updatedConfig = {
      ...projectDisplayConfig,
      dataTypes: {
        ...projectDisplayConfig.dataTypes,
        medication: {
          ...projectDisplayConfig.dataTypes.medication,
          selectedGroups: newValue.map((g) => ({
            tagName: g.tagName,
            displayName: g.displayName,
            medicationEnums: g.medicationEnums
          }))
        }
      }
    };
    onConfigurationChange(updatedConfig);
  };

  const getUnitForCategory = (category: string): string => {
    const units: { [key: string]: string } = {
      tremor_dyskinesia: "h/day",
      mobility: "h/day",
      sleep: "h/day",
      medication: "count/day"
    };
    return units[category] || "N/A";
  };

  const handleVisualizationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const updatedConfig = {
      ...projectDisplayConfig,
      visualization: {
        ...projectDisplayConfig.visualization,
        highlightMissingData: event.target.checked
      }
    };
    onConfigurationChange(updatedConfig);
  };

  const defaultDataTypes = {
    tremor_dyskinesia: { enabled: false, threshold: 6 },
    mobility: { enabled: false, threshold: 0 },
    sleep: { enabled: false, threshold: 1 },
    medication: { enabled: false, threshold: 0 },
    tap_test: { enabled: false },
    bowel_movement: { enabled: false }
  };

  const mergedDataTypes = {
    ...defaultDataTypes,
    ...projectDisplayConfig.dataTypes
  };

  const selectedMedications =
    projectDisplayConfig.dataTypes.medication.selectedMedications || [];
  const selectedGroups =
    projectDisplayConfig.dataTypes.medication.selectedGroups || [];

  // Map selected medications to MedicationOption objects for the Autocomplete value
  const selectedMedicationValues: MedicationOption[] = selectedMedications.map(
    (sm) => ({
      name: sm.name,
      classification: sm.classification
    })
  );

  const selectedGroupValues: MedicationTagGroup[] = selectedGroups.map(
    (sg) => ({
      tagName: sg.tagName,
      displayName: sg.displayName,
      medicationEnums: sg.medicationEnums
    })
  );

  return (
    <div>
      <Typography variant="h6">Data Options</Typography>
      <Typography>
        Please select the categories of data your project requires and,
        optionally, establish expected data thresholds, to help manage project
        health.
      </Typography>
      <FormGroup>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={4}>
            <Typography variant="subtitle1">
              <strong>DATA CATEGORIES</strong>
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="subtitle1">
              <strong>EXPECTED DATA</strong>
            </Typography>
          </Grid>
        </Grid>

        {Object.entries(mergedDataTypes).map(([key, config]) => (
          <div key={key}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.enabled}
                      onChange={handleCheckboxChange(
                        key as keyof ProjectDisplayConfig["dataTypes"]
                      )}
                    />
                  }
                  label={DISPLAY_LABELS[key] || key}
                />
              </Grid>
              {config.enabled && config.threshold !== undefined && (
                <Grid item xs={6} sm={2}>
                  <TextField
                    type="number"
                    value={config.threshold}
                    onChange={handleThresholdChange(
                      key as keyof ProjectDisplayConfig["dataTypes"]
                    )}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{
                      shrink: false
                    }}
                    variant="outlined"
                    sx={{
                      borderRadius: "32px"
                    }}
                    disabled
                  />
                </Grid>
              )}
              {config.enabled && (
                <Grid item xs={6} sm={2}>
                  <Typography>{getUnitForCategory(key)}</Typography>
                </Grid>
              )}
            </Grid>
            {key === "medication" && config.enabled && (
              <Grid container spacing={2} sx={{ pl: 4, pb: 1 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          (
                            projectDisplayConfig.dataTypes
                              .medication as MedicationDataTypeConfig
                          ).showAll ?? false
                        }
                        onChange={(e) => {
                          const updatedConfig = {
                            ...projectDisplayConfig,
                            dataTypes: {
                              ...projectDisplayConfig.dataTypes,
                              medication: {
                                ...projectDisplayConfig.dataTypes.medication,
                                showAll: e.target.checked
                              }
                            }
                          };
                          onConfigurationChange(updatedConfig);
                        }}
                      />
                    }
                    label="All Medications"
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Filter specific medications:
                  </Typography>
                  <Autocomplete
                    multiple
                    options={medicationOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.classification === value.classification
                    }
                    value={selectedMedicationValues}
                    onChange={(_, newValue) =>
                      handleMedicationSelectionChange(newValue)
                    }
                    loading={loadingMedications}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search medications..."
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingMedications && (
                                <CircularProgress size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                  {tagGroupOptions.length > 0 && (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2, mb: 1 }}
                      >
                        Filter by medication category:
                      </Typography>
                      <Autocomplete
                        multiple
                        options={tagGroupOptions}
                        getOptionLabel={(option) => option.displayName}
                        isOptionEqualToValue={(option, value) =>
                          option.tagName === value.tagName
                        }
                        value={selectedGroupValues}
                        onChange={(_, newValue) =>
                          handleGroupSelectionChange(newValue)
                        }
                        loading={loadingMedications}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search categories..."
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingMedications && (
                                    <CircularProgress size={20} />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            )}
          </div>
        ))}
      </FormGroup>

      <Typography variant="h6">Visualization</Typography>
      <Typography>Configure visualization options for your project.</Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={projectDisplayConfig.visualization.highlightMissingData}
              onChange={handleVisualizationChange}
            />
          }
          label="Highlight missing data"
        />
      </FormGroup>
    </div>
  );
};
